import { X } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Stepper } from '@/components/ui/stepper'
import { WizardNavigation } from '@/components/PlanWizard/WizardNavigation'
import { toast } from '@/stores/useToastStore'
import {
  useCltWizardStore,
  CLT_WIZARD_STEPS,
  LAST_STEP,
} from '@/stores/useCltWizardStore'
import { useUpsertTemplate } from '@/hooks/useCltTemplates'
import { collectInvalidRules } from './clt.mock'
import { StepDetails } from './StepDetails'
import { StepStrategyGroups } from './StepStrategyGroups'
import { StepLogicMatrix } from './StepLogicMatrix'
import { StepSchedule } from './StepSchedule'
import { StepReview } from './StepReview'
import { BackupDaysMatrixDialog } from './BackupDaysMatrixDialog'
import { CalendarLookupDialog } from './CalendarLookupDialog'

export function CltWizard() {
  const isOpen = useCltWizardStore(s => s.isOpen)
  const editingId = useCltWizardStore(s => s.editingId)
  const currentStep = useCltWizardStore(s => s.currentStep)
  const close = useCltWizardStore(s => s.close)
  const nextStep = useCltWizardStore(s => s.nextStep)
  const prevStep = useCltWizardStore(s => s.prevStep)
  const buildDraft = useCltWizardStore(s => s.buildDraft)
  const upsertMutation = useUpsertTemplate()

  // Validation inputs
  const name = useCltWizardStore(s => s.name)
  const biweeklyOffsetGroups = useCltWizardStore(s => s.biweeklyOffsetGroups)

  const strategyGroups = useCltWizardStore(s => s.strategyGroups)
  const matrices = useCltWizardStore(s => s.matrices)

  const invalidRuleCells = useMemo(
    () => collectInvalidRules(matrices),
    [matrices]
  )

  const canContinue = useMemo(() => {
    switch (currentStep) {
      case 0: // Details — name required
        return name.trim() !== ''
      case 1: // Strategy Groups — at least one group, each with >= 1 code
        return (
          strategyGroups.length > 0 &&
          strategyGroups.every(g => g.codes.length > 0)
        )
      case 2: // Milestone Logic — every DELTA / BACKUP_DAYS rule needs a valid reference milestone
        return invalidRuleCells.length === 0
      case 3: // Biweekly Offset — each group needs >=1 milestone and >=1 fully-set target
        return biweeklyOffsetGroups.every(
          g =>
            g.milestoneIds.length > 0 &&
            g.targets.length > 0 &&
            g.targets.every(t => t.strategyGroupId !== '')
        )
      default: // Review
        return name.trim() !== ''
    }
  }, [
    currentStep,
    name,
    strategyGroups,
    biweeklyOffsetGroups,
    invalidRuleCells,
  ])

  // Lock body scroll while the full-screen wizard is open.
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const isLastStep = currentStep === LAST_STEP

  const handleSubmit = () => {
    const draft = buildDraft()
    upsertMutation.mutate(draft, {
      onSuccess: () => {
        toast.success(
          editingId
            ? 'Calendar Logic Template updated'
            : 'Calendar Logic Template created'
        )
        close()
      },
    })
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <StepDetails />
      case 1:
        return <StepStrategyGroups />
      case 2:
        return <StepLogicMatrix />
      case 3:
        return <StepSchedule />
      case 4:
        return <StepReview />
      default:
        return null
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-white bg-white px-8 pt-4 pb-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-[#1c1c1c]">
            {editingId
              ? 'Edit Calendar Logic Template'
              : 'Create Calendar Logic Template'}
          </h1>
          <button
            onClick={close}
            className="cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stepper */}
      <div className="border-b border-white bg-white px-8 py-6 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)]">
        <Stepper steps={CLT_WIZARD_STEPS} currentStep={currentStep} />
      </div>

      {/* Step content */}
      <div className="min-h-0 max-w-full flex-1 overflow-y-auto bg-[#f8f8f8] px-4 py-8 md:px-8 lg:px-12">
        {renderStep()}
      </div>

      {/* Navigation */}
      <div className="border-t border-white bg-white px-8 py-[25px] shadow-[0px_-1px_3px_0px_rgba(0,0,0,0.1),0px_-1px_2px_-1px_rgba(0,0,0,0.1)]">
        <WizardNavigation
          onBack={prevStep}
          onContinue={nextStep}
          onSubmit={handleSubmit}
          canGoBack={currentStep > 0}
          canContinue={canContinue && !upsertMutation.isPending}
          isLastStep={isLastStep}
          submitLabel={editingId ? 'Save Changes' : 'Create Template'}
        />
      </div>

      {/* Global reference-data dialogs, opened from the value chooser */}
      <BackupDaysMatrixDialog />
      <CalendarLookupDialog />
    </div>
  )
}
